import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatOption,
  MatSelect,
  MatSelectModule,
} from '@angular/material/select';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { SynMultiSelectOption } from './syn-multi-select-option.model';

@Component({
  selector: 'syn-multi-select',
  templateUrl: './syn-multi-select.component.html',
  styleUrl: './syn-multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class SynMultiSelectComponent<T> implements OnChanges {
  constructor() {
    this.searchFormCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        debounceTime(100),
        distinctUntilChanged(),
        tap((value) => this._filterSearchOptions(value))
      )
      .subscribe();
  }

  protected readonly ALL_OPTION = '(All)';

  public matMultiSelectRef = viewChild<MatSelect>('matMultiSelect');

  public options = input<SynMultiSelectOption<T>[] | string[]>();
  public labelIcon = input.required<string>();
  public labelText = input.required<string>();
  public labelSearch = input<string>();
  public labelPosition = input<'before' | 'after'>('before');
  public theme = input<'default' | 'transparent'>('default');
  // Overlay panel width. A defined width lets the CDK position strategy
  // measure the panel and flip to its right-aligned fallback near the screen
  // edge, instead of overflowing. 'auto' matches the trigger width.
  public panelWidth = input<string | number | null>(320);

  public selectionsApplied = output<SynMultiSelectOption<T>[] | string[]>();

  protected selectFormCtrl = new FormControl();
  protected searchFormCtrl = new FormControl();

  protected filteredOptions = signal<SynMultiSelectOption<T>[] | string[]>([]);

  ngOnChanges(_changes: SimpleChanges): void {
    this._filterSearchOptions();
  }

  protected displayOption(option: string | SynMultiSelectOption<T>): string {
    return typeof option === 'string' ? option : option.label;
  }

  protected toggleAllChange(
    allOptionItem: MatOption<string>,
    event?: MatOptionSelectionChange<string>
  ): void {
    if (event?.isUserInput) {
      this.selectFormCtrl.setValue(
        allOptionItem.selected ? this.options() : []
      );
      if (allOptionItem.selected) {
        allOptionItem.select();
      }
    }
  }

  protected toggleOptionChange(
    optionItem: MatOption<string | SynMultiSelectOption<T>>,
    event?: MatOptionSelectionChange<string | SynMultiSelectOption<T>>
  ): void {
    if (event?.isUserInput) {
      const formValue = this.selectFormCtrl.value || [];
      const optionValue = optionItem.value;

      if (optionItem.selected) {
        this._addOptionToFormValue(formValue, optionValue);
      } else {
        this._removeOptionFromFormValue(formValue, optionValue);
      }

      if (
        formValue.length === this.options()?.length &&
        this.options()?.length != 1
      ) {
        this._addOptionToFormValue(formValue, this.ALL_OPTION);
      }
    }
  }

  protected applySelection(): void {
    this.selectionsApplied.emit(
      this.selectFormCtrl.value.filter(
        (value: string) => value !== this.ALL_OPTION
      )
    );
    this.matMultiSelectRef()?.close();
  }

  // Number of real selections shown on the trigger chip (excludes the
  // synthetic "(All)" marker value).
  protected selectedCount(): number {
    return (this.selectFormCtrl.value ?? []).filter(
      (value: string) => value !== this.ALL_OPTION
    ).length;
  }

  // True when every option is selected — the chip then reads "All".
  protected isAllSelected(): boolean {
    const total = this.options()?.length ?? 0;
    return total > 0 && this.selectedCount() === total;
  }

  private _addOptionToFormValue(
    formValue: (string | SynMultiSelectOption<T>)[],
    optionValue: string | SynMultiSelectOption<T>
  ): void {
    formValue.push(optionValue);
    this.selectFormCtrl.patchValue(formValue);
  }

  private _removeOptionFromFormValue(
    formValue: (string | SynMultiSelectOption<T>)[],
    optionValue: string | SynMultiSelectOption<T>
  ): void {
    const index = formValue.indexOf(optionValue);
    if (index > -1) {
      formValue.splice(index, 1);
    }
    if (formValue.includes(this.ALL_OPTION)) {
      formValue.splice(formValue.indexOf(this.ALL_OPTION), 1);
    }
    this.selectFormCtrl.patchValue(formValue);
  }

  private _filterSearchOptions(searchValue?: string): void {
    const options = this.options() ?? [];
    if (!searchValue) {
      this.filteredOptions.set(options);
      return;
    }

    const filteredStringOptions = options.filter(
      (option): option is string =>
        typeof option === 'string' &&
        option.toLowerCase().includes(searchValue.toLowerCase())
    );

    const filteredObjectOptions = options.filter(
      (option): option is SynMultiSelectOption<T> =>
        typeof option !== 'string' &&
        option.label.toLowerCase().includes(searchValue.toLowerCase())
    );

    this.filteredOptions.set(filteredStringOptions ?? filteredObjectOptions);
  }
}
